import {
  CHAT_TYPE,
  CHATBOT_TYPE,
  ROUTE_NAMES,
  FILE_TYPE,
  LEGAL_CHATMODE,
} from "@root/constants";
import axios from "axios";

export const isValidArray = (arr: TObject) =>
  arr && Array.isArray(arr) && arr.length;

export const isValidId = (id: TObject) => id && Number(id) > 0;

/**
 * Updates the `isExpended` property of the question at the specified index.
 * Sets the `isExpended` property to the provided value for the specified index,
 * and sets it to `false` for all other questions.
 *
 * @param {IFaqs[]} questions - The array of question objects.
 * @param {number} index - The index of the question to update.
 * @param {boolean} value - The boolean value to set for the `isExpended` property.
 * @returns {IFaqs[]} The updated array of question objects with the `isExpended` property updated.
 */
export const updateisExpended = (
  questions: IFaqs[],
  index: number,
  value: boolean
): IFaqs[] => {
  return questions.map((question, i) => ({
    ...question,
    isExpended: i === index ? value : false,
  }));
};

export const getLast4Digits = (cardNumber: string): string => {
  return cardNumber?.slice(-4);
};

/**
 * Adds a PDF ID to each item in the chat history array.
 * @param chatHistory - The original chat history array.
 * @param pdfId - The PDF ID to be added.
 * @returns A new chat history array with the PDF ID added to each item.
 */
export const addPdfIdToChatHistory = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  return chatHistory.map((chat) => ({
    ...chat,
    pdf_id: pdfId,
  }));
};

/**
 * Filters chat history records by a specific PDF ID.
 * @param chatHistory - The chat history array.
 * @param pdfId - The PDF ID to filter by.
 * @returns A new array containing only the records with the specified PDF ID.
 */
export const filterChatHistoryByPdfId = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  // Step 1: Filter the chat history based on the pdfId
  const filteredHistory = chatHistory.filter((chat) => chat?.pdf_id === pdfId);

  // Step 2: Remove pdf_id from each element in the filtered array
  const result = filteredHistory.map(({ pdf_id, ...rest }) => rest);

  return result;
};

export const carouselSliderProps = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 3,
  },
};

export function getYouTubeVideoId(url: string): string | null {
  if (url) {
    const regex =
      /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

    const match = url.match(regex);
    return match ? match[1] : null;
  }
  return null;
}

export function getDomainFromUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

/**
 * Safely parses a JSON string with error handling.
 * Returns a default value if parsing fails.
 *
 * @param jsonString - The JSON string to parse
 * @param defaultValue - Optional default value to return on parse failure (defaults to null)
 * @returns The parsed JSON object/array/value, or the default value if parsing fails
 */
export const safeJsonParse = <T = any>(
  jsonString: string | null | undefined,
  defaultValue: T | null = null
): T | null => {
  if (!jsonString || typeof jsonString !== "string") {
    return defaultValue;
  }

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error("JSON parse error:", error);
    return defaultValue;
  }
};

export const getCustomFileName = (fileName: string) => {
  const fileExtension = fileName.split(".").pop() || "";
  const customFileName = `kte-${Date.now()}${
    fileExtension ? "." + fileExtension : ""
  }`;
  return customFileName;
};

export const getChatRoute = (chat: any) => {
  if (chat?.type === CHAT_TYPE.KCS) {
    return `/kcs-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.PROJECT_CHATBOT) {
    return `/document-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.MULTI_DOCUMENTS_QUERY) {
    return `/checklist-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.DRAWING_ANALYSIS_CHAT) {
    return `/drawing-analysis-chat/${chat.id}`;
  } else {
    return `/chat/${chat.id}`;
  }
};

export const getChatRoutebythreadid = (chat: any) => {
  if (chat?.type === CHAT_TYPE.KCS) {
    return `/kcs-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.PROJECT_CHATBOT) {
    return `/document-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.MULTI_DOCUMENTS_QUERY) {
    return `/checklist-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.DRAWING_ANALYSIS_CHAT) {
    return `/drawing-analysis-chat/${chat.id}`;
  } else {
    return `/chat/${chat.id}`;
  }
};

export const getChatType = (pathname: string) => {
  if (pathname?.includes(ROUTE_NAMES.KCS)) {
    return 6;
  } else if (pathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS)) {
    return 5;
  } else if (pathname?.includes(ROUTE_NAMES.DOCUMENT)) {
    return 4;
  } else if (pathname?.includes(ROUTE_NAMES.CHECKLIST)) {
    return 3;
  } else if(pathname?.includes(ROUTE_NAMES.PERMIT_REVIEW)) {
    return 7;
  }else if(pathname?.includes(ROUTE_NAMES.MATERIAL_CAMPARISON)) {
    return 8;
  }else {
    return 1;
  }
};

/**
 * Gets the chatbot name based on chatbot ID and optional mode.
 * This is a reusable utility function that accepts a translation function.
 *
 * @param chatbotId - The chatbot type ID (1, 3, 4, 5, or 6)
 * @param t - The translation function from useTranslation hook
 * @param mode - Optional mode for law chatbot ("search" for law case, "chat" for law)
 * @returns The translated chatbot name
 */
export const GetChatbotName = (
  chatbotId: number,
  t: (key: string) => string,
  mode?: any
): string => {
  switch (String(chatbotId)) {
    case CHATBOT_TYPE.LAW_CHATBOT:
      return mode === LEGAL_CHATMODE.WEB_SEARCH
        ? t("translate.webSearch")
        : t("translate.lawInfoCentre");

    case CHATBOT_TYPE.PROJECT_DOCUMENT_CHATBOT:
      return t("chatHistory.projectDocument");

    case CHATBOT_TYPE.DRAWING_ANALYSIS_CHATBOT:
      return t("chatHistory.drawingAnalysis");

    case CHATBOT_TYPE.MULTI_DOCUMENT_CHATBOT:
      return t("chatHistory.multiDocument");

    case CHATBOT_TYPE.PERMIT_ITEM_EXTRACTION_CHATBOT:
      return t("reports.permitComplianceVerification");

    case CHATBOT_TYPE.KCS_CHATBOT:
      return t("sidebar.kcsChatbot");

    default:
      return t("chatHistory.unknown");
  }
};

export const getStatus = (status: any) => {
  if (status === 0) return "NOT_STARTED";
  if (status === 8) return "COMPLETED";
  if (status === 9) return "FAILED";
  return "PROCESSING";
};


export const calculateStrength = (value: string) => {
  let strength = 0;
  if (value.length >= 8) strength += 25;
  if (/[A-Z]/.test(value)) strength += 25;
  if (/\d/.test(value)) strength += 25;
  if (/[@$!%*?&#]/.test(value)) strength += 25;
  return strength;
};

/**
 * Determines the file type based on the current pathname/route.
 * @param pathname - The current pathname from useLocation hook
 * @returns The appropriate FILE_TYPE constant
 */
export const getFileType = (pathname: string) => {
  if (pathname === ROUTE_NAMES.PERMIT_REVIEW) {
    return FILE_TYPE.EXTRACTION_REVIEW;
  }
  if (pathname === ROUTE_NAMES.LEGAL_REVIEW) {
    return FILE_TYPE.EXCEL_AUTO_COMPLETE;
  }
  if (pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) {
    return FILE_TYPE.DRAWING_ANALYSIS;
  }
  if (pathname.startsWith(ROUTE_NAMES.CHECKLIST)) {
    return FILE_TYPE.MULTI_DOC;
  }
  return FILE_TYPE.SIMPLE_FILE;
};

/**
 * Handles navigation to start a new chat based on the current pathname.
 * This is a reusable utility function that determines the appropriate route
 * to navigate to for starting a new chat session.
 *
 * @param pathname - The current pathname from useLocation hook
 * @param navigate - The navigate function from useNavigate hook
 */
export const handleStartChat = (
  pathname: string,
  navigate: (path: string) => void
) => {
  if (pathname.includes("/document-chat/")) {
    navigate(ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT);
  } else if (pathname.includes("/chat/")) {
    navigate(ROUTE_NAMES.LEGAL_CHATBOT);
  } else if (pathname.includes("/drawing-analysis-chat/")) {
    navigate(ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD);
  } else if (pathname.includes("/kcs-chat/")) {
    navigate(ROUTE_NAMES.KCS);
  }
};

// ==================== Chunk Upload Utilities ====================

export interface ChunkInfo {
  chunk: Blob;
  currentChunk: number;
  totalChunks: number;
  start: number;
  end: number;
  fileSize: number;
}

/**
 * Splits a file into chunks of specified size.
 * @param file - The file to split
 * @param chunkSize - Size of each chunk in bytes
 * @returns Array of chunk information objects
 */
export const splitFileIntoChunks = (
  file: File,
  chunkSize: number
): ChunkInfo[] => {
  const chunks: ChunkInfo[] = [];
  const totalChunks = Math.ceil(file.size / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);

    chunks.push({
      chunk: file.slice(start, end),
      currentChunk: i + 1,
      totalChunks,
      start,
      end,
      fileSize: file.size,
    });
  }

  return chunks;
};

/**
 * Creates FormData for a chunk upload request.
 * @param chunkInfo - Information about the chunk to upload
 * @param metadata - Metadata including mimeType, uploadId, fileKey, and fileName
 * @returns FormData object ready for upload
 */
export const createChunkFormData = (
  chunkInfo: ChunkInfo,
  metadata: {
    mimeType: string;
    uploadId?: string;
    fileKey?: string;
    fileName: string;
  }
): FormData => {
  const formData = new FormData();

  formData.append("chunk", chunkInfo.chunk, metadata.fileName);
  formData.append("currentChunk", chunkInfo.currentChunk.toString());
  formData.append("totalChunks", chunkInfo.totalChunks.toString());
  formData.append("start", chunkInfo.start.toString());
  formData.append("end", chunkInfo.end.toString());
  formData.append("fileSize", chunkInfo.fileSize.toString());
  formData.append("mimeType", metadata.mimeType);
  formData.append("fileName", metadata.fileName);

  if (metadata.uploadId) {
    formData.append("uploadId", metadata.uploadId);
  }
  if (metadata.fileKey) {
    formData.append("fileKey", metadata.fileKey);
  }

  return formData;
};

/**
 * Uploads a file in chunks sequentially.
 * @param file - The file to upload
 * @param originalFormData - Original FormData containing other fields (userId, projectId, etc.)
 * @param options - Upload options including headers, baseUrl, endpoint, timeout, chunkSize, progress callback, and abort signal
 * @returns Promise resolving to the final upload result
 */
export const uploadFileInChunks = async (
  file: File,
  originalFormData: FormData,
  options: {
    headers: Record<string, any>;
    baseUrl: string;
    endpoint: string;
    timeout: number;
    chunkSize?: number;
    onProgress?: (progress: number) => void;
    signal?: AbortSignal;
  }
): Promise<any> => {
  const {
    headers,
    baseUrl,
    endpoint,
    timeout,
    chunkSize = 5 * 1024 * 1024, // Default 5MB
    onProgress,
    signal,
  } = options;

  const chunks = splitFileIntoChunks(file, chunkSize);

  let uploadId: string | undefined;
  let fileKey: string | undefined;
  let finalResult: any;

  // Upload chunks sequentially
  for (let i = 0; i < chunks.length; i++) {
    // Check if upload was aborted
    if (signal?.aborted) {
      throw new Error("Upload aborted");
    }

    const chunkInfo = chunks[i];
    const chunkFormData = createChunkFormData(chunkInfo, {
      mimeType: file.type || "application/octet-stream",
      uploadId,
      fileKey,
      fileName: file.name,
    });

    // Copy other fields from original FormData (userId, projectId, etc.)
    originalFormData.forEach((value, key) => {
      if (key !== "file") {
        chunkFormData.append(key, value);
      }
    });

    const response = await axios.post(`${baseUrl}${endpoint}`, chunkFormData, {
      headers: {
        ...headers,
        "Content-Type": undefined, // Let browser set boundary
      },
      timeout,
      signal,
    });

    const result = response.data;
    const data = result?.data?.data || result?.data || result;

    // Capture uploadId and fileKey from first chunk response
    if (data?.uploadId) uploadId = data.uploadId;
    if (data?.fileKey) fileKey = data.fileKey;

    // Store final result (last chunk returns document ID)
    if (data?.id || data?.documentId) {
      finalResult = data;
    }

    // Update progress
    const progress = Math.round(((i + 1) / chunks.length) * 100);
    if (onProgress) {
      onProgress(progress);
    }
  }

  return finalResult || { uploadId, fileKey };
};

/**
 * Adds labels (A, B, C, etc.) and color schemes to an array of file objects.
 * @param originalArray - The array of file objects to process
 * @returns A new array with added label and colorScheme properties
 */
export const addLabelsAndColors = (originalArray: any[]) => {
  if (!originalArray || !Array.isArray(originalArray)) {
    return [];
  }
  const colorSchemes = ["red", "cyan", "purple"];
  return originalArray.map((obj: any, index: number) => ({
    ...obj,
    label: String.fromCharCode(65 + index), // 65 is ASCII for 'A'
    colorScheme: colorSchemes[index % colorSchemes.length],
  }));
};